import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSmartBack } from "@/lib/useSmartBack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Package,
  FileText,
  Target,
  Sparkles,
  AlignLeft,
  Link as LinkIcon,
  Lightbulb,
  FileUp,
  Video,
  Info,
} from "lucide-react";
import { LoadingPlanet } from "@/components/LoadingPlanet";
import { Notice } from "@/components/ui";
import { friendlyError } from "@/lib/friendlyError";
import { useCompanyId } from "@/lib/CompanyContext";
import {
  knowledgeApi,
  type KnowledgeBaseType,
  type KnowledgeBaseSourceKind,
  type ReferenceType,
} from "@/api/knowledge";

// Tipos de referência — cada card. Estágio 1: criação manual + campos no
// KbDetail. Estágios 2-4 ligam scrape/IA/transcrição por tipo.
const REFERENCE_TYPE_OPTIONS: {
  value: ReferenceType;
  label: string;
  description: string;
}[] = [
  { value: "autoridade", label: "Autoridade", description: "Um perfil/marca que é referência no que faz. A IA entende por que é autoridade e o que aplicar." },
  { value: "conteudo", label: "Conteúdo (vídeo)", description: "Uma referência de vídeo: os formatos, os ganchos e o jeito de conduzir a narrativa que prendem a atenção." },
  { value: "criativo", label: "Criativo (visual)", description: "Referência de estilo visual: paleta, layout, tratamento. Gabarito pras artes." },
  { value: "sazonal", label: "Sazonal (campanha)", description: "Criativos de uma campanha específica que você curte (ex: Black Friday)." },
  { value: "landing_page", label: "Estrutura de página", description: "Uma landing page ou site que você curte como gabarito de ESTRUTURA: a anatomia (seções), a persuasão e a hierarquia. A IA extrai o esqueleto, sem copiar a copy." },
  { value: "custom", label: "Custom", description: "Qualquer outra referência que não cabe acima." },
];
import { queryKeys } from "@/lib/queryKeys";

/**
 * Fluxo de criação de KB modular.
 *
 * 2 passos enxutos:
 *  1. Tipo (Produto, Material, Concorrente, Custom)
 *  2. Conteúdo: nome, descrição, fonte (manual/url/pdf/csv)
 *
 * Anexar uma KB a uma task é decisão do usuário no momento da criação
 * da task (em /nova-tarefa) — sem triggers automáticos.
 */

const TYPE_OPTIONS: {
  value: KnowledgeBaseType;
  label: string;
  description: string;
  icon: typeof Package;
  color: string;
}[] = [
  {
    value: "product",
    label: "Produto",
    description: "Uma oferta sua: o que vende, pra quem, preço, garantia e os diferenciais. Vira a base das tarefas desse produto.",
    icon: Package,
    color: "var(--nd-orange)",
  },
  {
    value: "material",
    label: "Material de apoio",
    description: "Qualquer conteúdo que ajuda os agentes: um PDF, um link, a transcrição de uma reunião, uma planilha.",
    icon: FileText,
    color: "var(--nd-orange)",
  },
  {
    value: "competitor",
    label: "Concorrente",
    description: "Análise de empresa competidora — perfil, anúncios, posicionamento e oportunidades pra você se diferenciar.",
    icon: Target,
    color: "var(--nd-orange)",
  },
  {
    value: "reference",
    label: "Referência",
    description: "Uma inspiração que você curte (não é concorrente): conteúdo, narrativa, autoridade, criativo, sazonal. A IA extrai o estilo, sem copiar.",
    icon: Lightbulb,
    color: "var(--nd-orange)",
  },
  {
    value: "custom",
    label: "Custom",
    description: "Qualquer outro contexto que não cabe nas categorias acima",
    icon: Sparkles,
    color: "var(--nd-orange)",
  },
];

const SOURCE_OPTIONS: {
  value: KnowledgeBaseSourceKind;
  label: string;
  description: string;
  icon: typeof AlignLeft;
  available: boolean;
}[] = [
  {
    value: "manual",
    label: "Texto",
    description: "Cole ou escreva o conteúdo direto",
    icon: AlignLeft,
    available: true,
  },
  {
    value: "url",
    label: "URL",
    description: "Analisa a página agora e popula tudo automaticamente",
    icon: LinkIcon,
    available: true,
  },
  {
    value: "pdf",
    label: "PDF / CSV",
    description: "Upload de arquivo. IA lê e extrai resumo, palavras-chave e contexto.",
    icon: FileUp,
    available: true,
  },
  {
    value: "video",
    label: "Vídeo",
    description: "Upload com transcrição automática. Em breve.",
    icon: Video,
    available: false,
  },
];

interface NovaKbState {
  type: KnowledgeBaseType | null;
  name: string;
  description: string;
  sourceKind: KnowledgeBaseSourceKind;
  contentText: string;
  url: string;
  file: File | null;
  /** Pra type=competitor: qual fonte usar pra analisar o concorrente */
  competitorSource: "instagram" | "url";
  /** Pra type=competitor + source=instagram: handle do IG (sem o @) */
  competitorHandle: string;
  /** Pra type=competitor + source=url: site/LP do concorrente */
  competitorUrl: string;
  /** Pra type=reference: qual tipo de referência (autoridade/conteúdo/...) */
  referenceType: ReferenceType | null;
  /** Pra type=reference: fonte (@ do Instagram, link de 1 post, ou manual) */
  referenceSource: "instagram" | "link" | "manual";
  /** Pra type=reference + source=instagram: @ do perfil de referência */
  referenceHandle: string;
  /** Pra type=reference + source=link: URL de 1 post específico */
  referencePostUrl: string;
}

const EMPTY: NovaKbState = {
  type: null,
  name: "",
  description: "",
  sourceKind: "manual",
  contentText: "",
  url: "",
  file: null,
  competitorSource: "instagram",
  competitorHandle: "",
  competitorUrl: "",
  referenceType: null,
  referenceSource: "instagram",
  referenceHandle: "",
  referencePostUrl: "",
};

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 14,
  fontFamily: "var(--wf-font-body)",
  color: "var(--wf-ink)",
  width: "100%",
  outline: "none",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 140,
  resize: "vertical" as const,
  lineHeight: 1.5,
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)", letterSpacing: "-0.005em" }}
      >
        {label}
      </span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -3 }}>{hint}</span>
      )}
      {children}
    </label>
  );
}

/** Heurística: o texto tem ESTRUTURA de URL (não é só um @ do Instagram)?
 *  Só bloqueia sinais FORTES de link — protocolo, www, barra/espaço/:/?/@ no
 *  meio. NÃO bloqueia por sufixo tipo TLD: handles do IG aceitam ponto e podem
 *  terminar em ".br", ".fit", etc (ex: exemplo.br, joao.silva, maria.fit) —
 *  bloquear isso impedia raspar perfis válidos (bug reportado pelo time). Se
 *  alguém colar um domínio cru (loja.com.br) sem http/www/barra, a raspagem só
 *  não acha o perfil (falha clara), o que é bem mais raro que um @ com ponto. */
function looksLikeUrl(raw: string): boolean {
  const h = raw.replace(/^@+/, "").trim();
  if (/[\s/:?@]/.test(h)) return true; // barra, espaço, :, ?, @ no meio = estrutura de URL
  if (/^https?/i.test(raw) || /^www\./i.test(h)) return true;
  return false;
}

export default function NovaKb() {
  const navigate = useNavigate();
  const goBack = useSmartBack("/briefing");
  // Mostra o motivo do bloqueio (ex: "Anexe o arquivo PDF") quando a pessoa
  // tenta avançar/criar sem preencher o necessário.
  const [showBlockHint, setShowBlockHint] = useState(false);
  // Drag-and-drop do arquivo (antes arrastar abria o PDF numa aba do browser).
  const [dragActive, setDragActive] = useState(false);
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Tipo vindo da query string (?type=product|competitor|material) — quando
  // o user clica "+ Adicionar" estando numa aba específica do Briefing, pula
  // o step de escolher tipo.
  const initialTypeParam = searchParams.get("type") as KnowledgeBaseType | null;
  const hasInitialType =
    initialTypeParam === "product" ||
    initialTypeParam === "competitor" ||
    initialTypeParam === "material";
  const [stepIdx, setStepIdx] = useState(hasInitialType ? 1 : 0);
  const [state, setState] = useState<NovaKbState>(() => ({
    ...EMPTY,
    type: hasInitialType ? initialTypeParam : null,
    // Materiais começam direto em PDF (caso de uso principal). Produto e
    // concorrente continuam em manual/url.
    sourceKind: initialTypeParam === "material" ? "pdf" : "manual",
  }));

  const set = (patch: Partial<NovaKbState>) => setState((prev) => ({ ...prev, ...patch }));

  // Se o type vier por query string, garante coerência mesmo em mudanças
  // de URL durante a sessão.
  useEffect(() => {
    if (hasInitialType && state.type !== initialTypeParam) {
      setState((prev) => ({ ...prev, type: initialTypeParam }));
    }
  }, [hasInitialType, initialTypeParam, state.type]);

  const create = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("no company");
      if (!state.type) throw new Error("type required");

      // Concorrente: pipeline dedicado. 2 fontes possíveis.
      if (state.type === "competitor") {
        if (state.competitorSource === "url") {
          const url = state.competitorUrl.trim();
          if (!url) throw new Error("Informe a URL do site");
          return knowledgeApi.analyzeCompetitorByUrl(companyId, url);
        }
        if (!state.competitorHandle.trim()) throw new Error("Informe o @handle do Instagram");
        return knowledgeApi.analyzeCompetitor(companyId, state.competitorHandle.trim());
      }

      // Produto via PDF: a IA extrai os campos da oferta e cria a KB de produto.
      if (state.type === "product" && state.sourceKind === "pdf") {
        if (!state.file) throw new Error("Selecione um arquivo");
        return knowledgeApi.extractProductPdf(companyId, state.file, {
          name: state.name.trim() || undefined,
        });
      }

      // Upload de arquivo (PDF/CSV) usa endpoint dedicado com multipart +
      // análise Haiku síncrona — vira KB de material.
      if (state.sourceKind === "pdf" || state.sourceKind === "csv") {
        if (!state.file) throw new Error("Selecione um arquivo");
        return knowledgeApi.uploadMaterial(companyId, state.file, {
          description: state.description || undefined,
        });
      }

      // Referência: por @ do Instagram (perfil), link de 1 post, ou manual.
      if (state.type === "reference") {
        if (state.referenceType === "landing_page") {
          // Manual → cria a referência vazia e leva pra tela de detalhe (a pessoa
          // preenche a estrutura ali, igual às outras referências). URL → o backend
          // dispara o analyzer (Firecrawl) em type=reference + referenceType=landing_page.
          if (state.sourceKind === "manual") {
            return knowledgeApi.create(companyId, {
              type: "reference",
              name: state.name,
              description: state.description || null,
              sourceKind: "manual",
              contentText: "",
              contentMeta: { reference: { referenceType: "landing_page" } },
            });
          }
          return knowledgeApi.create(companyId, {
            type: "reference",
            name: state.name,
            description: state.description || null,
            sourceKind: "url",
            contentText: "",
            contentMeta: {
              reference: { referenceType: "landing_page" },
              source: { originalUrl: state.url.trim() },
            },
          });
        }
        if (state.referenceSource === "instagram") {
          return knowledgeApi.analyzeReferenceInstagram(companyId, {
            handle: state.referenceHandle.trim(),
            referenceType: state.referenceType ?? "custom",
            name: state.name.trim() || undefined,
          });
        }
        if (state.referenceSource === "link") {
          return knowledgeApi.analyzeReferencePost(companyId, {
            postUrl: state.referencePostUrl.trim(),
            referenceType: state.referenceType ?? "criativo",
            name: state.name.trim() || undefined,
          });
        }
        return knowledgeApi.create(companyId, {
          type: "reference",
          name: state.name,
          description: state.description || null,
          sourceKind: "manual",
          contentText: "",
          contentMeta: {
            reference: { referenceType: state.referenceType ?? "custom" },
          },
        });
      }

      const finalContent =
        state.sourceKind === "url"
          ? `Fonte (URL): ${state.url}\n\n${state.contentText}`.trim()
          : state.contentText;
      return knowledgeApi.create(companyId, {
        type: state.type,
        name: state.name,
        description: state.description || null,
        sourceKind: state.sourceKind,
        contentText: finalContent,
        contentMeta:
          state.sourceKind === "url"
            ? { source: { originalUrl: state.url } }
            : {},
      });
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({
        queryKey: companyId ? queryKeys.companies.knowledge(companyId) : [],
      });
      // Produto (manual ou PDF) e Referência: vão direto pra tela de edição pra
      // preencher/revisar os campos. Outros tipos voltam pro briefing.
      const goToDetail =
        (state.type === "product" && (state.sourceKind === "manual" || state.sourceKind === "pdf")) ||
        state.type === "reference";
      if (
        goToDetail &&
        created &&
        typeof created === "object" &&
        "id" in created &&
        typeof (created as { id: unknown }).id === "string"
      ) {
        navigate(`/briefing/${(created as { id: string }).id}`);
        return;
      }
      navigate("/briefing");
    },
  });

  // Motivo do bloqueio (mensagem DIRETA do que falta) — null = pode avançar.
  // Antes o botão só desabilitava sem dizer nada (ex: criar base de PDF sem
  // anexar arquivo não dava feedback nenhum).
  const blockReason = useMemo<string | null>(() => {
    if (stepIdx === 0) return state.type ? null : "Escolha o tipo de base pra continuar.";
    if (stepIdx === 1) {
      if (state.type === "competitor") {
        if (state.competitorSource === "url") {
          return state.competitorUrl.trim().length > 5 ? null : "Cole a URL do concorrente pra continuar.";
        }
        const h = state.competitorHandle.trim();
        if (h.length <= 1) return "Informe o @ do concorrente pra continuar.";
        // O campo é só do @ — uma URL (página de vendas) aqui não funciona e
        // colhia dados errados em silêncio. Bloqueia e mostra a saída certa.
        if (looksLikeUrl(h))
          return "Aqui vai só o @ do Instagram, sem link. Ex: @nomedoperfil. Pra usar um site/página de vendas, troque a fonte pra “Site”.";
        return null;
      }
      if (state.type === "reference") {
        if (!state.referenceType) return "Escolha o tipo de referência pra continuar.";
        if (state.referenceType === "landing_page") {
          if (state.sourceKind === "manual") return null;
          const u = state.url.trim();
          return /^https?:\/\/.+\..+/i.test(u)
            ? null
            : "Cole a URL da landing page pra continuar (ou troque pra “Preencher manual”).";
        }
        if (state.referenceSource === "instagram") {
          const h = state.referenceHandle.trim();
          if (h.length <= 1) return "Informe o @ do Instagram pra continuar.";
          if (looksLikeUrl(h))
            return "Aqui vai só o @ do perfil, sem link. Ex: @nomedoperfil. Pra um post específico, troque a fonte pra “Link de post”.";
          return null;
        }
        if (state.referenceSource === "link") {
          return /instagram\.com\/(p|reel|tv)\//i.test(state.referencePostUrl.trim())
            ? null
            : "Cole a URL de um post do Instagram pra continuar.";
        }
        return state.name.trim().length > 0 ? null : "Dê um nome pra essa referência pra continuar.";
      }
      // PDF/CSV não exige nome (IA gera). Só precisa do ARQUIVO.
      if (state.sourceKind === "pdf" || state.sourceKind === "csv") {
        return state.file ? null : `Anexe o arquivo ${state.sourceKind.toUpperCase()} pra continuar.`;
      }
      if (!state.name.trim()) return "Dê um nome pra essa base pra continuar.";
      if (state.sourceKind === "manual" && state.type !== "product" && !state.contentText.trim()) {
        return "Escreva o conteúdo da base pra continuar.";
      }
      if (state.sourceKind === "url") {
        const u = state.url.trim();
        if (!u) return "Cole a URL da fonte pra continuar.";
        // A análise de página lê um SITE — perfil do Instagram não funciona (e só
        // dava erro DEPOIS de criar). Bloqueia na hora com a saída certa.
        if (/instagram\.com/i.test(u))
          return "Pra produto, use a URL do site ou da página de vendas — não um perfil do Instagram. (Pra analisar um Instagram, crie uma base de Referência ou Concorrente.)";
        if (!/^https?:\/\/[^\s.]+\.[^\s]+/i.test(u))
          return "Cole uma URL válida, começando com https:// (ex: https://seusite.com.br).";
      }
      return null;
    }
    return null;
  }, [stepIdx, state]);

  const canProceedFromStep = blockReason === null;

  const isLast = stepIdx === 1;

  const stepLabels = ["Tipo", "Conteúdo"];

  if (!companyId) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando empresa…</span>
      </div>
    );
  }

  const isExtractingProductPdf =
    create.isPending && state.type === "product" && state.sourceKind === "pdf";
  const isAnalyzingProduct =
    create.isPending && state.type === "product" &&
    (state.sourceKind === "url" || state.sourceKind === "pdf");
  const isUploadingFile =
    create.isPending && state.type !== "product" &&
    (state.sourceKind === "pdf" || state.sourceKind === "csv");
  const isAnalyzingCompetitor = create.isPending && state.type === "competitor";
  const isAnalyzingReference =
    create.isPending &&
    state.type === "reference" &&
    (state.referenceSource === "instagram" || state.referenceSource === "link");
  const showLoadingOverlay =
    isAnalyzingProduct || isUploadingFile || isAnalyzingCompetitor || isAnalyzingReference;
  const loadingTitle = isAnalyzingReference
    ? "Analisando a referência"
    : isAnalyzingCompetitor
      ? "Analisando o concorrente"
      : isUploadingFile
        ? "Analisando o documento"
        : "Analisando o produto";
  const loadingSubtitle = isAnalyzingReference
    ? "Lendo o perfil no Instagram, baixando os melhores posts e transcrevendo os melhores Reels pra montar o dossiê de referência. Pode levar até 2 minutos."
    : isAnalyzingCompetitor
      ? "Lendo o perfil no Instagram, buscando anúncios na Meta Ads Library, raspando o site se houver, e escrevendo um dossiê personalizado pra sua realidade. Pode levar até 2 minutos."
      : isUploadingFile
        ? "Lendo o arquivo, extraindo nome, descrição, palavras-chave e resumo. Pode levar até 30 segundos."
        : isExtractingProductPdf
          ? "Lendo o PDF, extraindo preço, promessa, mecanismo único, personas, concorrentes e mais. Pode levar até 30 segundos."
          : "Lendo a página, extraindo preço, promessa, mecanismo único, personas, concorrentes e mais. Pode levar até 30 segundos.";

  return (
    <div>
      {/* Overlay de análise — aparece durante create.isPending pra produto+URL ou upload */}
      <AnimatePresence>
        {showLoadingOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(5,8,16,0.78)",
              backdropFilter: "blur(8px)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              padding: 32,
            }}
          >
            <motion.div
              initial={{ scale: 0.92, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 24,
                maxWidth: 420,
                textAlign: "center",
              }}
            >
              <LoadingPlanet size={120} />
              <div>
                <div
                  className="wf-display"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: "-0.015em",
                    color: "#fff",
                    marginBottom: 6,
                  }}
                >
                  {loadingTitle}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.6,
                  }}
                >
                  {loadingSubtitle}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero compacto */}
      <div
        style={{
          background: "linear-gradient(90deg, #050810 0%, #0a1020 50%, #1a1424 100%)",
          color: "#fff",
          padding: "32px 32px 24px",
          borderBottom: "1px solid var(--wf-ink-5)",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              fontWeight: 600,
              color: "rgba(255,255,255,0.55)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 10,
            }}
          >
            <Plus size={13} strokeWidth={2} color="var(--nd-orange)" />
            Nova base de conhecimento
          </div>
          <h1
            className="wf-display"
            style={{
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            {stepLabels[stepIdx]}
          </h1>
          <p
            style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.65)",
              marginTop: 6,
              fontWeight: 500,
            }}
          >
            {stepIdx === 0 && "Que tipo de contexto você está adicionando"}
            {stepIdx === 1 && "Conteúdo da base. Você anexa às tasks que precisarem dela."}
          </p>

          <div style={{ marginTop: 18 }}>
            <div
              style={{
                height: 3,
                background: "rgba(255,255,255,0.1)",
                borderRadius: 999,
                overflow: "hidden",
                marginBottom: 10,
              }}
            >
              <motion.div
                style={{ height: "100%", background: "var(--nd-orange)", borderRadius: 999 }}
                animate={{ width: `${((stepIdx + 1) / 2) * 100}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "28px 32px 48px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {/* Step 1: Type */}
          {stepIdx === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              {TYPE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = state.type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set({ type: opt.value })}
                    className="wf-card"
                    style={{
                      cursor: "pointer",
                      padding: 16,
                      background: selected ? "#fff8ef" : "var(--wf-paper)",
                      border: selected
                        ? "1px solid var(--nd-orange)"
                        : "1px solid var(--wf-ink-5)",
                      boxShadow: selected ? "0 0 0 3px rgba(255,112,0,0.10)" : "none",
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      transition: "border-color 120ms",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 8,
                          background: `${opt.color}18`,
                          color: opt.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon size={15} strokeWidth={1.75} />
                      </div>
                      <span
                        className="wf-display"
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "var(--wf-ink)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {opt.label}
                      </span>
                    </div>
                    <span style={{ fontSize: 12, color: "var(--wf-ink-2)", lineHeight: 1.4 }}>
                      {opt.description}
                    </span>
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* Step 2: Conteúdo */}
          {stepIdx === 1 && state.type === "competitor" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{ display: "flex", flexDirection: "column", gap: 18 }}
            >
              {/* Source toggle — Instagram OU URL */}
              <Field
                label="Como você conhece esse concorrente?"
                hint="Escolha a fonte que você tem em mãos. Se ele tem Instagram, prefira essa fonte (vem mais rica). Se é uma empresa que vive no site, escolha URL."
              >
                <div
                  role="radiogroup"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  {(
                    [
                      {
                        value: "instagram" as const,
                        label: "Instagram (@)",
                        sub: "Posts, anúncios, tom",
                      },
                      {
                        value: "url" as const,
                        label: "Site ou Landing",
                        sub: "Posicionamento, oferta, copy",
                      },
                    ]
                  ).map((opt) => {
                    const checked = state.competitorSource === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        onClick={() => set({ competitorSource: opt.value })}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          background: checked ? "rgba(255,112,0,0.06)" : "var(--wf-paper)",
                          border: checked
                            ? "1.5px solid var(--nd-orange)"
                            : "1px solid var(--wf-ink-5)",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          color: "inherit",
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          transition: "border-color 120ms",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: checked ? "var(--nd-orange)" : "var(--wf-ink)",
                          }}
                        >
                          {opt.label}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "var(--wf-ink-3)",
                          }}
                        >
                          {opt.sub}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Field>

              {/* Input dinâmico conforme fonte */}
              {state.competitorSource === "instagram" ? (
                <Field
                  label="Instagram do concorrente"
                  hint="Apenas o @ (sem URL completa). Vamos ler o perfil, anúncios da Meta Ads Library e site (se houver) pra montar um dossiê."
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "var(--wf-paper)",
                      border: "1px solid var(--wf-ink-5)",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        padding: "9px 0 9px 14px",
                        fontSize: 14,
                        color: "var(--wf-ink-3)",
                        fontFamily: "var(--wf-font-body)",
                        fontWeight: 500,
                      }}
                    >
                      @
                    </span>
                    <input
                      value={state.competitorHandle}
                      onChange={(e) =>
                        set({
                          competitorHandle: e.target.value
                            .replace(/^@/, "")
                            .replace(/^https?:\/\/(www\.)?instagram\.com\//, "")
                            .replace(/\/$/, ""),
                        })
                      }
                      placeholder="marcelotoledo"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: 14,
                        fontFamily: "var(--wf-font-body)",
                        color: "var(--wf-ink)",
                        padding: "9px 12px 9px 2px",
                      }}
                    />
                  </div>
                </Field>
              ) : (
                <Field
                  label="Site ou landing page do concorrente"
                  hint="Cole a URL completa. Vamos ler a página e extrair posicionamento, oferta, copy, prova social e CTA."
                >
                  <input
                    type="url"
                    value={state.competitorUrl}
                    onChange={(e) => set({ competitorUrl: e.target.value })}
                    placeholder="https://empresax.com.br/landing"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    style={inputStyle}
                  />
                </Field>
              )}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  padding: 12,
                  background: "#fff8ef",
                  border: "1px solid var(--nd-orange)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--wf-ink-2)",
                  lineHeight: 1.55,
                }}
              >
                <Info
                  size={14}
                  strokeWidth={1.75}
                  color="var(--nd-orange)"
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                <div>
                  {state.competitorSource === "instagram"
                    ? "Esse processo leva 1 a 2 minutos e custa créditos da sua equipe. Vamos coletar até 30 posts e 20 anúncios ativos, e você recebe um dossiê com o que está funcionando pra esse concorrente, adaptado ao seu posicionamento."
                    : "Esse processo leva cerca de 30-60 segundos. Vamos ler a página completa e montar um dossiê focado em posicionamento declarado, oferta, copy de venda, prova social e CTA. Se a página bloquear leitura ou tiver pouco conteúdo, te avisamos pra escolher outra URL."}
                </div>
              </div>
            </motion.div>
          )}

          {/* Referência: escolhe o TIPO (autoridade/conteúdo/...) + nome.
              O dossiê é preenchido no KbDetail. */}
          {stepIdx === 1 && state.type === "reference" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{ display: "flex", flexDirection: "column", gap: 18 }}
            >
              <Field label="Tipo de referência" hint="Cada tipo tem um foco — a IA extrai o que importa pra ele">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 8,
                  }}
                >
                  {REFERENCE_TYPE_OPTIONS.map((opt) => {
                    const selected = state.referenceType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          set({
                            referenceType: opt.value,
                            // Landing page abre com a fonte "URL" por padrão
                            // (automático); manual é o fallback.
                            ...(opt.value === "landing_page" ? { sourceKind: "url" as const } : {}),
                          })
                        }
                        style={{
                          cursor: "pointer",
                          padding: "12px 14px",
                          background: selected ? "#fff8ef" : "var(--wf-paper)",
                          border: selected
                            ? "1px solid var(--nd-orange)"
                            : "1px solid var(--wf-ink-5)",
                          borderRadius: 8,
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--wf-ink)" }}>
                          {opt.label}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", lineHeight: 1.4 }}>
                          {opt.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Field>
              {state.referenceType !== "landing_page" && (
              <Field label="Como você quer trazer essa referência?">
                <div role="radiogroup" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {(
                    [
                      { value: "instagram" as const, label: "Instagram (@)", sub: "A IA lê os posts do perfil e monta o dossiê" },
                      { value: "link" as const, label: "Link de 1 post", sub: "Cole o link de um criativo específico que você curtiu" },
                      { value: "manual" as const, label: "Preencher manual", sub: "Você escreve os campos (avançado)" },
                    ]
                  ).map((opt) => {
                    const selected = state.referenceSource === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => set({ referenceSource: opt.value })}
                        style={{
                          cursor: "pointer",
                          padding: "12px 14px",
                          background: selected ? "#fff8ef" : "var(--wf-paper)",
                          border: selected ? "1px solid var(--nd-orange)" : "1px solid var(--wf-ink-5)",
                          borderRadius: 8,
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--wf-ink)" }}>{opt.label}</span>
                        <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{opt.sub}</span>
                      </button>
                    );
                  })}
                </div>
              </Field>
              )}

              {state.referenceType === "landing_page" ? (
                <>
                  <Field label="Como você quer trazer essa página?">
                    <div role="radiogroup" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {(
                        [
                          { value: "url" as const, label: "Pela URL", sub: "A IA raspa a landing page e monta o dossiê automaticamente" },
                          { value: "manual" as const, label: "Preencher manual", sub: "Você escreve a estrutura na próxima tela (se a URL der erro)" },
                        ]
                      ).map((opt) => {
                        const selected = state.sourceKind === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => set({ sourceKind: opt.value })}
                            style={{
                              cursor: "pointer",
                              padding: "12px 14px",
                              background: selected ? "#fff8ef" : "var(--wf-paper)",
                              border: selected ? "1px solid var(--nd-orange)" : "1px solid var(--wf-ink-5)",
                              borderRadius: 8,
                              textAlign: "left",
                              display: "flex",
                              flexDirection: "column",
                              gap: 3,
                            }}
                          >
                            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--wf-ink)" }}>{opt.label}</span>
                            <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{opt.sub}</span>
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  {state.sourceKind !== "manual" && (
                    <Field label="URL da landing page / site" hint="A IA raspa a página e monta o dossiê de estrutura: seções, persuasão e hierarquia. Não copia a copy.">
                      <input
                        style={inputStyle}
                        value={state.url}
                        onChange={(e) => set({ url: e.target.value })}
                        placeholder="https://..."
                      />
                    </Field>
                  )}
                </>
              ) : state.referenceSource === "instagram" ? (
                <Field label="Instagram da referência" hint="A IA lê os posts de melhor desempenho e monta o dossiê.">
                  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 12,
                        fontSize: 14,
                        color: "var(--wf-ink-3)",
                        pointerEvents: "none",
                        fontFamily: "var(--wf-font-body)",
                      }}
                    >
                      @
                    </span>
                    <input
                      style={{ ...inputStyle, paddingLeft: 26 }}
                      value={state.referenceHandle.replace(/^@+/, "")}
                      onChange={(e) => set({ referenceHandle: e.target.value })}
                      placeholder="perfil que te inspira (sem o @)"
                    />
                  </div>
                </Field>
              ) : state.referenceSource === "link" ? (
                <Field label="Link do post" hint="Cole o link de um post ou reel específico do Instagram que você quer usar de referência.">
                  <input
                    style={inputStyle}
                    value={state.referencePostUrl}
                    onChange={(e) => set({ referencePostUrl: e.target.value })}
                    placeholder="https://www.instagram.com/p/..."
                  />
                </Field>
              ) : (
                <Field label="Nome" hint="Como você quer chamar essa referência">
                  <input
                    style={inputStyle}
                    value={state.name}
                    onChange={(e) => set({ name: e.target.value })}
                    placeholder="Ex: Referência de conteúdo que admiro"
                  />
                </Field>
              )}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  padding: 12,
                  background: "#fff8ef",
                  border: "1px solid var(--nd-orange)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--wf-ink-2)",
                  lineHeight: 1.55,
                }}
              >
                <Info size={14} strokeWidth={1.75} color="var(--nd-orange)" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  {state.referenceType === "landing_page"
                    ? "A IA raspa a landing page (Firecrawl) e monta o dossiê de estrutura: seções, gatilhos de persuasão e hierarquia. Você revisa na tela seguinte."
                    : state.referenceSource === "instagram"
                      ? "A IA raspa o perfil, baixa os melhores posts pra biblioteca visual, transcreve os melhores Reels (narrativa) e pré-preenche o dossiê. Você revisa e ajusta na tela seguinte."
                      : "Ao criar, te levamos pra tela da referência onde você preenche os campos e sobe os criativos manualmente."}
                </div>
              </div>
            </motion.div>
          )}

          {stepIdx === 1 && state.type !== "competitor" && state.type !== "reference" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{ display: "flex", flexDirection: "column", gap: 18 }}
            >
              <Field label="Nome" hint="Como você quer chamar essa base">
                <input
                  style={inputStyle}
                  value={state.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="Ex: Produto Mentoria Premium"
                />
              </Field>
              <Field label="Descrição" hint="Resumo curto, opcional">
                <input
                  style={inputStyle}
                  value={state.description}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="Ex: Curso de marketing 12 semanas com mentoria semanal"
                />
              </Field>

              <Field label="Fonte do conteúdo">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 8,
                  }}
                >
                  {SOURCE_OPTIONS.filter((opt) => {
                    // Upload de PDF: material (vira KB de material) e produto
                    // (a IA extrai os campos da oferta). Concorrente vem por
                    // URL/Instagram. CSV continua só pra material.
                    if (opt.value === "pdf" && state.type !== "material" && state.type !== "product") {
                      return false;
                    }
                    if (opt.value === "csv" && state.type !== "material") {
                      return false;
                    }
                    return true;
                  }).map((opt) => {
                    const Icon = opt.icon;
                    const selected = state.sourceKind === opt.value;
                    const disabled = !opt.available;
                    const isProductPdf = opt.value === "pdf" && state.type === "product";
                    const label = isProductPdf ? "PDF" : opt.label;
                    const description = isProductPdf
                      ? "IA lê o PDF da oferta e extrai os campos do produto"
                      : opt.description;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && set({ sourceKind: opt.value })}
                        style={{
                          cursor: disabled ? "not-allowed" : "pointer",
                          padding: "12px 14px",
                          background: selected ? "#fff8ef" : "var(--wf-paper)",
                          border: selected
                            ? "1px solid var(--nd-orange)"
                            : "1px solid var(--wf-ink-5)",
                          borderRadius: 8,
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          opacity: disabled ? 0.5 : 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon size={13} strokeWidth={1.75} color="var(--nd-orange)" />
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
                        </div>
                        <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
                          {description}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Field>

              {state.sourceKind === "manual" && state.type === "product" && (
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: 12,
                    background: "#fff8ef",
                    border: "1px solid var(--nd-orange)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "var(--wf-ink-2)",
                    lineHeight: 1.55,
                  }}
                >
                  <Info
                    size={14}
                    strokeWidth={1.75}
                    color="var(--nd-orange)"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <div>
                    Ao criar, te levamos pra tela completa de produto onde você
                    preenche manualmente os mesmos campos que a IA preencheria
                    via URL: preço, promessa, mecanismo único, personas,
                    garantia, identidade visual etc.
                  </div>
                </div>
              )}
              {state.sourceKind === "manual" && state.type !== "product" && (
                <Field label="Conteúdo" hint="Texto que vai pro contexto do agente">
                  <textarea
                    style={textareaStyle}
                    value={state.contentText}
                    onChange={(e) => set({ contentText: e.target.value })}
                    placeholder="Cole ou escreva o conteúdo aqui..."
                  />
                  <span
                    style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: 4 }}
                  >
                    {state.contentText.length.toLocaleString("pt-BR")} caracteres
                  </span>
                </Field>
              )}

              {state.sourceKind === "url" && (
                <>
                  <Field
                    label="URL do produto"
                    hint={
                      state.type === "product"
                        ? "Ao criar, vamos analisar a página agora. Extraímos preço, promessa, mecanismo único, personas, concorrentes e tudo mais. Você revisa e ajusta depois."
                        : "Link da página"
                    }
                  >
                    <input
                      style={inputStyle}
                      value={state.url}
                      onChange={(e) => set({ url: e.target.value })}
                      placeholder="https://..."
                      type="url"
                    />
                  </Field>
                  {state.type !== "product" && (
                    <Field
                      label="Notas adicionais"
                      hint="Contexto extra além da URL, opcional"
                    >
                      <textarea
                        style={{ ...textareaStyle, minHeight: 80 }}
                        value={state.contentText}
                        onChange={(e) => set({ contentText: e.target.value })}
                        placeholder="Observações sobre o conteúdo da URL..."
                      />
                    </Field>
                  )}
                </>
              )}

              {(state.sourceKind === "pdf" || state.sourceKind === "csv") && (
                <Field
                  label="Arquivo"
                  hint={
                    state.type === "product"
                      ? "PDF da oferta (proposta, ebook, página salva) até 20MB. A IA lê e extrai preço, promessa, mecanismo único, personas e mais."
                      : "PDF, CSV ou TXT até 20MB. A IA lê e extrai nome, descrição, palavras-chave e resumo automaticamente."
                  }
                >
                  <label
                    htmlFor="kb-file-input"
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!dragActive) setDragActive(true);
                    }}
                    onDragEnter={(e) => e.preventDefault()}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) set({ file: f });
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 10,
                      padding: 24,
                      border:
                        dragActive || state.file
                          ? "1.5px solid var(--nd-orange)"
                          : "1.5px dashed var(--wf-ink-4)",
                      borderRadius: 10,
                      background: dragActive || state.file ? "#fff8ef" : "var(--wf-paper)",
                      cursor: "pointer",
                      transition: "border-color 120ms, background 120ms",
                    }}
                  >
                    <FileUp size={22} strokeWidth={1.75} color="var(--nd-orange)" />
                    {state.file ? (
                      <>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--wf-ink)",
                          }}
                        >
                          {state.file.name}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
                          {(state.file.size / 1024).toLocaleString("pt-BR", {
                            maximumFractionDigits: 1,
                          })}{" "}
                          KB · clique pra trocar
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--wf-ink)",
                          }}
                        >
                          {dragActive ? "Solte o arquivo aqui" : "Clique ou arraste um arquivo aqui"}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
                          PDF, CSV, TXT até 20MB
                        </div>
                      </>
                    )}
                    <input
                      id="kb-file-input"
                      type="file"
                      accept=".pdf,.csv,.txt,.md,.json,.tsv,application/pdf,text/csv,text/plain"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) set({ file: f });
                      }}
                      style={{ display: "none" }}
                    />
                  </label>
                </Field>
              )}
            </motion.div>
          )}

          {/* Nav */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 28,
              paddingTop: 16,
              borderTop: "1px solid var(--wf-ink-5)",
            }}
          >
            <button
              type="button"
              className="wf-btn wf-btn--ghost wf-btn--sm"
              onClick={() => {
                // Step 0 (ou primeira tela quando type veio por query string):
                // sai pra PÁGINA ANTERIOR (de onde veio). Senão, volta um step.
                if (stepIdx === 0 || hasInitialType) {
                  goBack();
                } else {
                  setStepIdx((i) => Math.max(0, i - 1));
                }
              }}
              style={{ display: "flex", alignItems: "center", gap: 5 }}
            >
              <ArrowLeft size={12} strokeWidth={2} /> Voltar
            </button>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="wf-btn wf-btn--ghost wf-btn--sm"
              onClick={() => navigate("/briefing")}
            >
              Cancelar
            </button>
            {isLast ? (
              <button
                type="button"
                className="wf-btn wf-btn--primary"
                disabled={create.isPending}
                onClick={() => {
                  if (blockReason) {
                    setShowBlockHint(true);
                    return;
                  }
                  setShowBlockHint(false);
                  create.mutate();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: canProceedFromStep && !create.isPending ? 1 : 0.55,
                  cursor: !canProceedFromStep || create.isPending ? "not-allowed" : "pointer",
                }}
              >
                {create.isPending
                  ? state.type === "product" && state.sourceKind === "url"
                    ? "Analisando página…"
                    : "Criando…"
                  : "Criar base"}
                <Check size={13} strokeWidth={2} color="#fff" />
              </button>
            ) : (
              <button
                type="button"
                className="wf-btn wf-btn--primary"
                onClick={() => {
                  if (blockReason) {
                    setShowBlockHint(true);
                    return;
                  }
                  setShowBlockHint(false);
                  setStepIdx((i) => Math.min(1, i + 1));
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: canProceedFromStep ? 1 : 0.55,
                  cursor: canProceedFromStep ? "pointer" : "not-allowed",
                }}
              >
                Próximo <ArrowRight size={13} strokeWidth={2} color="#fff" />
              </button>
            )}
          </div>

          {showBlockHint && blockReason && (
            <div style={{ marginTop: 12 }}>
              <Notice tone="danger">{blockReason}</Notice>
            </div>
          )}

          {create.isError && (
            <div style={{ marginTop: 12 }}>
              <Notice tone="danger" title="Não consegui criar a base">
                {friendlyError((create.error as Error)?.message)}
              </Notice>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
